import { IframeQueryParameters } from '@wix/wix-chatbot-common/browser';
import React, { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useCategories } from '../../../common/hooks/useCategories';
import { Page, useCurrentPage } from '../../../common/hooks/useCurrentPage';
import { ILiveChatChangeMode } from '../../../common/messageCreators';
import { selectBiData, selectItems } from '../../../common/selectors/chat';
import {
  selectFullPageSidebarExtended,
  selectFullPageSidebarOpened,
} from '../../../common/selectors/helpCenter';
import { selectOrigParams } from '../../../common/selectors/meta';
import { homepageService } from '../../../common/services/homepage/homepageService';
import { MESSAGES } from '../../../constants';
import { IBIHelpCenter, PresentedState } from '../../../util/bi';

export type BIHelpCenterMonitorProps = PropsWithChildren<{
  biHelpCenter: IBIHelpCenter;
}>;

// Here we are watching on "chatbot parameters"(conversationId, nodeId etc.) and keep BIHelpCenter updated with these parameters
// Also there is PresentedState handler(chatbot, search results etc.)
export const BIHelpCenterMonitor: FC<BIHelpCenterMonitorProps> = ({
  biHelpCenter,
  children,
}) => {
  const { search, state } = useLocation();
  const currentPage = useCurrentPage();
  const { currentCategory } = useCategories();
  const { conversation_id, nodeId, intentId, mainOptionName } = useSelector(
    selectBiData,
  );
  const items = useSelector(selectItems);
  const isFullPageSidebarExtended = useSelector(selectFullPageSidebarExtended);
  const isFullPageSidebarOpened = useSelector(selectFullPageSidebarOpened);
  const changeModeItem = items.find(
    (item) => item.type === MESSAGES.CHANGE_MODE,
  ) as ILiveChatChangeMode;

  useEffect(() => {
    if (conversation_id) {
      biHelpCenter.conversationId = conversation_id;
    }
  }, [conversation_id]);

  useEffect(() => {
    if (nodeId) {
      biHelpCenter.nodeId = nodeId;
    }
  }, [nodeId]);

  useEffect(() => {
    if (intentId) {
      biHelpCenter.intentId = intentId;
    }
  }, [intentId]);

  useEffect(() => {
    if (mainOptionName) {
      biHelpCenter.mainOptionName = mainOptionName;
    }
  }, [mainOptionName]);

  useEffect(() => {
    if (changeModeItem?.ticketId) {
      biHelpCenter.ticketId = changeModeItem.ticketId;
    }
  }, [changeModeItem]);

  const { origPageName, origPlatform } = useSelector(selectOrigParams);

  useEffect(() => {
    if (origPageName) {
      biHelpCenter.origPageName = origPageName;
    }
  }, [origPageName]);

  useEffect(() => {
    if (origPlatform) {
      biHelpCenter.origPlatform = origPlatform;
    }
  }, [origPlatform]);

  useEffect(() => {
    biHelpCenter.fullPageSidebarExtended = isFullPageSidebarExtended;
  }, [isFullPageSidebarExtended]);

  useEffect(() => {
    biHelpCenter.fullPageSidebarOpened = isFullPageSidebarOpened;
  }, [isFullPageSidebarOpened]);

  // TODO:bi review setting currentPresentedState
  const shouldSkipCurrentStateTrack = useCallback(
    (page: Page) => {
      // need to wait for currentCategory
      // can not check is it SolutionList or SubCategoriesList without currentCategory
      if (page === Page.Category && !currentCategory) {
        return true;
      }
      // need to wait for currentCategory to be cleared
      // otherwise AllHelpTopics will be set twice
      if (page === Page.Categories && currentCategory) {
        return true;
      }

      return false;
    },
    [currentCategory],
  );

  useEffect(() => {
    if (shouldSkipCurrentStateTrack(currentPage)) {
      return;
    }

    const queryParams = new URLSearchParams(search);
    // TODO: remove when specs.engage.enableHomepage will be merged
    const isChatbotOpen =
      currentPage === Page.Chatbot ||
      (!homepageService.homepageEnabled &&
        queryParams.get(IframeQueryParameters.isChatbotOpen) !== 'false');
    const isSolutionList =
      !currentCategory?.children || currentCategory?.children.length === 0;

    biHelpCenter.searchedText = queryParams.get('query') ?? undefined;
    biHelpCenter.initPageEngagementEventInterval();

    if (isChatbotOpen) {
      biHelpCenter.currentPresentedState = PresentedState.Chatbot;
    } else if (currentPage === Page.Home) {
      biHelpCenter.currentPresentedState = PresentedState.Homepage;
    } else if (currentPage === Page.Categories) {
      biHelpCenter.currentPresentedState = PresentedState.AllHelpTopics;
    } else if (currentPage === Page.Category && isSolutionList) {
      biHelpCenter.currentPresentedState = PresentedState.SolutionList;
    } else if (currentPage === Page.Category) {
      biHelpCenter.currentPresentedState = PresentedState.SubCategoriesList;
    } else if (currentPage === Page.Search) {
      biHelpCenter.currentPresentedState = PresentedState.SearchResults;
    } else if (currentPage === Page.Article) {
      biHelpCenter.currentPresentedState = PresentedState.SingleArticle;
    }
  }, [search, currentPage, state, currentCategory]);

  return <>{children}</>;
};
